import { ReportAverageCheckData } from 'app/dataTypes/reports/data/common/averageCheck'
import { OrgId } from 'app/common/types/id'

import { AverageCheckDayValues, AverageCheckDto, AverageCheckTotals } from './data.type'

export type AverageCheckResultDto = AverageCheckDto

type AverageCheckItem = ReportAverageCheckData['orgs'][number]['total']

const roundTo2 = (value: number): number => Math.round(value * 100) / 100

const sumDayValues = (days: Array<string>, values: AverageCheckDayValues): number =>
  days.reduce((acc, day) => acc + (values[day] ?? 0), 0)

const calcAverageCheck = (revenue: number, salesCount: number): number =>
  salesCount === 0 ? 0 : roundTo2(revenue / salesCount)

const convertDayValuesByDaysIndex = (
  days: Array<string>,
  revenueByDay: AverageCheckDayValues,
  salesCountByDay: AverageCheckDayValues,
): Array<AverageCheckItem> =>
  days.map((day) => {
    const revenue = revenueByDay[day] ?? 0
    const salesCount = salesCountByDay[day] ?? 0
    return {
      revenueByDay: roundTo2(revenue),
      salesCountByDay: roundTo2(salesCount),
      averageCheck: calcAverageCheck(revenue, salesCount),
    }
  })

const convertTotalsByDays = (days: Array<string>, totals: AverageCheckTotals): AverageCheckItem => {
  const totalRevenue = sumDayValues(days, totals.revenue_by_day)
  const totalSalesCount = sumDayValues(days, totals.sales_count_by_day)
  return {
    revenueByDay: roundTo2(totalRevenue),
    salesCountByDay: roundTo2(totalSalesCount),
    averageCheck: calcAverageCheck(totalRevenue, totalSalesCount),
  }
}

export const convertResultAverageCheck = (data: AverageCheckDto): ReportAverageCheckData => {
  const { days, schools, totals } = data

  const orgs = schools.map(
    ({
      school_id: schoolId,
      school_title: schoolTitle,
      revenue_by_day: revenueByDay,
      sales_count_by_day: salesCountByDay,
    }) => ({
      id: String(schoolId) as OrgId,
      title: schoolTitle,
      dataByDateIndex: convertDayValuesByDaysIndex(days, revenueByDay, salesCountByDay),
      total: convertTotalsByDays(days, {
        revenue_by_day: revenueByDay,
        sales_count_by_day: salesCountByDay,
      }),
    }),
  )

  return {
    dates: [...days],
    orgs,
    orgsTotalByDateIndex: convertDayValuesByDaysIndex(
      days,
      totals.revenue_by_day,
      totals.sales_count_by_day,
    ),
    totalOverall: convertTotalsByDays(days, totals),
  }
}
